import { useCallback, useEffect, useState, type ReactNode } from "react";
import App from "./App";
import { MobileBottomNav } from "./components/MobileBottomNav";
import { HeaderActionProvider, MobileHeader } from "./components/MobileHeader";
import { PullToRefresh } from "./components/PullToRefresh";
import DevConsole from "./DevConsole";
import DeveloperPortal from "./developer/DeveloperApp";
import { fetchSession, setCsrf, setSessionToken, type Developer } from "./developer/api";
import { syncNativeAppClass, useNativeApp } from "./lib/mobile-ui";
import { notifyNativeAppReady } from "./lib/native-bridge";
import { runRegisteredRefresh } from "./lib/page-lifecycle";
import { CamerasPage } from "./pages/CamerasPage";
import { VideoWallPage } from "./pages/VideoWallPage";
import { LiveTripPage } from "./pages/LiveTripPage";
import { LoginPage } from "./pages/LoginPage";
import { PlaybackPage } from "./pages/PlaybackPage";
import { ProfilePage } from "./pages/ProfilePage";
import { SetupPage } from "./pages/SetupPage";
import { SdkHubPage } from "./pages/SdkHubPage";
import { SettingsPage } from "./pages/SettingsPage";
import { UsersAdminPage } from "./pages/UsersAdminPage";

syncNativeAppClass();

function keepAppQuery(): string {
  const app = new URLSearchParams(window.location.search).get("app");
  return app === "1" || app === "mobile" ? `?app=${encodeURIComponent(app)}` : "";
}

function hrefFor(to: string): { path: string; href: string } {
  const url = new URL(to, window.location.origin);
  const path = normalizePath(url.pathname);
  const params = new URLSearchParams(url.search);
  const app = new URLSearchParams(window.location.search).get("app");
  if (app === "1" || app === "mobile") params.set("app", app);
  const qs = params.toString();
  return { path, href: qs ? `${path}?${qs}` : path };
}

function pathOf(): string {
  return window.location.pathname.replace(/\/+$/, "") || "/";
}

function normalizePath(path: string): string {
  if (path === "/developers/login") return "/login";
  if (path === "/developers/setup") return "/setup";
  if (path === "/developers" || path === "/developer") return "/developer";
  if (path.startsWith("/developers/")) return `/developer/${path.slice("/developers/".length)}`;
  return path;
}

function navFor(me: { role: string }): Array<{ to: string; label: string; match?: (p: string) => boolean }> {
  const items: Array<{ to: string; label: string; match?: (p: string) => boolean }> = [
    { to: "/", label: "Dashboard", match: (p) => p === "/" || p === "/dashboard" },
    { to: "/video-wall", label: "Video Wall" },
    { to: "/cameras", label: me.role === "superadmin" ? "Cameras" : "My Cameras" },
    { to: "/playback", label: "Playback" },
  ];
  if (me.role === "superadmin") {
    items.push({ to: "/users", label: "Users", match: (p) => p === "/users" || p.startsWith("/users/") });
  }
  items.push(
    { to: "/developer", label: "Developer", match: (p) => p === "/developer" || p === "/developer/overview" },
    { to: "/developer/api-keys", label: "API Keys" },
  );
  if (me.role === "superadmin") {
    items.push({
      to: "/developer/web-sdk",
      label: "SDK",
      match: (p) => p.startsWith("/developer/web-sdk") || p.startsWith("/developer/flutter-sdk") || p === "/developer/sdk",
    });
  }
  items.push(
    { to: "/developer/docs", label: "API Documentation" },
    { to: "/settings", label: "Settings" },
  );
  return items;
}

export default function MasterApp() {
  const native = useNativeApp();
  const [path, setPath] = useState(() => normalizePath(pathOf()));
  const [urlSearch, setUrlSearch] = useState(() => window.location.search);
  const [me, setMe] = useState<Developer | null>(null);
  const [ready, setReady] = useState(false);
  const [keepCameras, setKeepCameras] = useState(() => normalizePath(pathOf()) === "/cameras");
  const camerasOpen = path === "/cameras";

  useEffect(() => {
    const onPop = () => {
      setPath(normalizePath(pathOf()));
      setUrlSearch(window.location.search);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  useEffect(() => {
    const current = pathOf();
    if (current === "/developers/login") {
      window.history.replaceState({}, "", `/login${keepAppQuery()}`);
    } else if (current === "/developers/setup") {
      window.history.replaceState({}, "", `/setup${window.location.search}`);
    } else if (current.startsWith("/developers")) {
      window.history.replaceState({}, "", normalizePath(current) + window.location.search);
    }
    void fetchSession()
      .then((session) => {
        setMe(session?.developer ?? null);
      })
      .catch(() => {
        setMe(null);
      })
      .finally(() => {
        setReady(true);
      });
  }, []);

  useEffect(() => {
    if (me && path === "/login") {
      window.history.replaceState({}, "", `/${keepAppQuery()}`);
      setPath("/");
    }
  }, [me, path]);

  useEffect(() => {
    if (camerasOpen) setKeepCameras(true);
  }, [camerasOpen]);

  useEffect(() => {
    if (!me) setKeepCameras(false);
  }, [me]);

  const onNativeRefresh = useCallback(async () => {
    try {
      const session = await fetchSession();
      if (session?.developer) setMe(session.developer);
    } catch {
      /* page handlers still refresh their own data */
    }
    await runRegisteredRefresh();
  }, []);

  useEffect(() => {
    if (!ready) return;
    void notifyNativeAppReady();
  }, [ready]);

  const go = (to: string, opts?: { replace?: boolean }) => {
    const next = hrefFor(to);
    if (opts?.replace) window.history.replaceState({}, "", next.href);
    else window.history.pushState({}, "", next.href);
    setPath(next.path);
    setUrlSearch(new URL(next.href, window.location.origin).search);
  };

  const logout = async () => {
    try {
      await fetch("/api/auth/logout", { method: "POST", credentials: "include", headers: { "X-CSRF-Token": sessionStorage.getItem("dc_dev_csrf") || "" } });
    } catch {
      /* ignore */
    }
    setCsrf("");
    setSessionToken("");
    setMe(null);
    window.history.pushState({}, "", `/login${keepAppQuery()}`);
    setPath("/login");
  };

  if (!ready) {
    if (native) {
      return <div className="app native-boot" aria-hidden="true" />;
    }
    return (
      <div className="app">
        <div className="banner">Loading…</div>
      </div>
    );
  }

  if (path === "/setup" || pathOf() === "/developers/setup") {
    return <SetupPage />;
  }

  if (!me) {
    if (path !== "/login") {
      window.history.replaceState({}, "", `/login${keepAppQuery()}`);
    }
    return <LoginPage native={native} onLoggedIn={(d) => { setMe(d); go("/"); }} />;
  }

  if (path === "/dev" && !native) {
    return <DevConsole />;
  }

  const openPlayback = (device: { id: string }) => go(`/playback?device=${encodeURIComponent(device.id)}`);
  const openLiveTrip = (device: { id: string }, from: "dashboard" | "cameras") =>
    go(`/live-trip?device=${encodeURIComponent(device.id)}&from=${from}`);
  const playbackDeviceId = new URLSearchParams(urlSearch).get("device");

  let page: ReactNode;
  if (path === "/cameras") page = native ? null : <CamerasPage onOpenPlayback={openPlayback} onOpenLiveTrip={(d) => openLiveTrip(d, "cameras")} />;
  else if (path === "/video-wall") page = <VideoWallPage go={go} />;
  else if (path === "/playback") page = <PlaybackPage go={go} deviceId={playbackDeviceId} />;
  else if (path === "/live-trip") page = <LiveTripPage go={go} deviceId={playbackDeviceId} onOpenPlayback={openPlayback} />;
  else if (path === "/users" || path.startsWith("/users/")) page = me.role === "superadmin" ? <UsersAdminPage me={me} /> : <App onOpenPlayback={openPlayback} onOpenLiveTrip={(d) => openLiveTrip(d, "dashboard")} />;
  else if (path === "/profile") page = native ? <ProfilePage me={me} onLogout={() => void logout()} onAccountUpdated={setMe} /> : <SettingsPage me={me} />;
  else if (path === "/sdk") page = native ? <SdkHubPage go={go} /> : <DeveloperPortal me={me} path="/developer/web-sdk" go={go} />;
  else if (path === "/settings/password") page = native ? <SettingsPage me={me} native section="password" /> : <SettingsPage me={me} />;
  else if (path === "/settings/email") page = native ? <SettingsPage me={me} native section="email" go={go} onAccountUpdated={setMe} /> : <SettingsPage me={me} />;
  else if (path === "/settings/smtp") page = native ? <SettingsPage me={me} native section="smtp" /> : <SettingsPage me={me} />;
  else if (path === "/settings") page = native ? <SettingsPage me={me} native section="menu" go={go} /> : <SettingsPage me={me} />;
  else if (path === "/dev") page = <DevConsole />;
  else if (path.startsWith("/developer")) page = <DeveloperPortal me={me} path={path} go={go} />;
  else page = <App onOpenPlayback={openPlayback} onOpenLiveTrip={(d) => openLiveTrip(d, "dashboard")} onOpenVideoWall={() => go("/video-wall")} />;

  if (native) {
    const mountCameras = camerasOpen || keepCameras;
    return (
      <div className="app master-shell native-shell">
        <HeaderActionProvider>
          <MobileHeader path={path} go={go} me={me} />
          <div className="master-body mobile-body">
            <PullToRefresh enabled onRefresh={onNativeRefresh}>
              {mountCameras ? (
                <div className={camerasOpen ? undefined : "native-page-hidden"} aria-hidden={!camerasOpen}>
                  <CamerasPage active={camerasOpen} onOpenPlayback={openPlayback} onOpenLiveTrip={(d) => openLiveTrip(d, "cameras")} />
                </div>
              ) : null}
              {page}
            </PullToRefresh>
          </div>
        </HeaderActionProvider>
        <MobileBottomNav path={path} go={go} me={me} />
      </div>
    );
  }

  return (
    <div className="app master-shell">
      <header className="topbar master-topbar">
        <div>
          <h1>Dashcam Platform</h1>
          <p>
            {me.name} · {me.role === "superadmin" ? "Super Admin" : "User"}
          </p>
        </div>
        <nav className="master-nav" aria-label="Main">
          {navFor(me).map((item) => {
            const active = item.match ? item.match(path) : path === item.to;
            return (
              <button key={item.to} type="button" className={active ? "primary" : ""} onClick={() => go(item.to)}>
                {item.label}
              </button>
            );
          })}
          <a className="topbar-link" href="/dev">
            Dev logs
          </a>
          <button type="button" onClick={() => void logout()}>
            Logout
          </button>
        </nav>
      </header>
      <div className="master-body">{page}</div>
    </div>
  );
}
